import React from 'react';
import MarkdownBlogLayout from '../../components/MarkdownBlogLayout';

const BlogCaseAcceptanceRevenueLeftBehind = () => {
  const content = `
## The Treatment Plan That Walked Out the Door

Every day, patients leave your practice with treatment plans in hand. They nod along as you explain the procedure. They say they'll "think about it" or "call to schedule." They walk out the door.

And then... nothing.

**60% of treatment plans never get scheduled.** That's not a typo. More than half of the dentistry you diagnose never happens—not because patients don't need it, but because life gets in the way and no one follows up.

Here's the uncomfortable truth: **Your unscheduled treatment is worth more than your marketing budget.**

---

## The Follow-Up Problem

Let's do some quick math:

### The Average Practice's Unscheduled Treatment

| Metric | Value |
|--------|-------|
| Treatment plans presented per month | 80 |
| Average treatment value | $1,200 |
| Total treatment presented | $96,000 |
| Acceptance rate (industry average) | 40% |
| **Unscheduled treatment per month** | **$57,600** |
| **Unscheduled treatment per year** | **$691,200** |

You're sitting on nearly **$700,000 in diagnosed, accepted-in-principle treatment** that never gets scheduled.

Why?

---

## Why Patients Don't Schedule

It's not that patients don't want the treatment. Research shows the top reasons patients don't schedule are:

### 1. Life Gets in the Way
They leave your office with good intentions. Then their kid gets sick, work gets busy, and that crown becomes "something I'll deal with later."

### 2. Financial Concerns
They need time to figure out how to pay for it. But without someone reaching out to discuss options, they never take the next step.

### 3. Fear and Anxiety
Dental anxiety is real. Without gentle encouragement, anxious patients will procrastinate indefinitely.

### 4. They Simply Forget
No malice, no decision—just the chaos of daily life pushing dental work down the priority list.

### 5. No One Asked
This is the biggest one. **If your team doesn't follow up, patients assume it wasn't that important.**

---

## The Follow-Up Gap

Here's what typically happens with treatment plan follow-up:

**Week 1:** Maybe a call, if staff remembers  
**Week 2:** Probably nothing  
**Week 3+:** Completely forgotten

Your front desk is busy. They're answering phones, checking patients in, handling insurance issues, and putting out fires. Following up on treatment plans from three weeks ago? That's the task that always gets pushed to "when I have time."

Spoiler: They never have time.

---

## The Compounding Cost of Inaction

Unscheduled treatment doesn't just represent lost revenue—it represents:

### Clinical Consequences
- Small cavities become root canals
- Cracks become extractions
- Periodontal disease progresses
- Patients end up in pain, in your chair, for emergency visits

### Financial Consequences
- Lost production you've already diagnosed
- Emergency visits that disrupt your schedule
- Patients who eventually go elsewhere
- Treatment that becomes more complex (and harder to accept)

### Relationship Consequences
- Patients feel forgotten
- Trust erodes
- They assume you don't really care about their health
- They become "inactive" patients

---

## Enter Kim: AI Treatment Coordinator

What if every patient with unscheduled treatment received personalized, persistent follow-up—without your staff lifting a finger?

**Kim is Asha's AI Treatment Coordinator**, and she's obsessed with one thing: turning diagnosed treatment into scheduled appointments.

### How Kim Works

**1. Automatic Treatment Tracking**
Kim monitors Asha PMS for unscheduled treatment. Every patient who leaves with a treatment plan goes into her workflow automatically.

**2. Intelligent Timing**
Kim doesn't blast patients with messages the moment they leave. She uses smart timing:
- Initial follow-up: 48-72 hours (while the visit is fresh)
- Second touch: 1 week (gentle reminder)
- Third touch: 2 weeks (address concerns)
- Ongoing: Monthly until scheduled or declined

**3. Personalized Outreach**
Kim's messages aren't generic. She references:
- The specific treatment discussed
- The tooth/teeth involved
- The doctor's recommendation
- Financial options available

**4. Multi-Channel Communication**
Kim reaches patients where they are:
- Text messages (highest response rate)
- Email (for detailed information)
- Phone calls (for patients who prefer voice)

**5. Objection Handling**
When patients respond with concerns, Kim addresses them:
- "It's too expensive" → Payment plan options
- "I'm scared" → Sedation options, gentle approach
- "I'm too busy" → Flexible scheduling, early/late appointments
- "I need to think about it" → Educational content about why treatment matters

**6. Seamless Scheduling**
When a patient is ready, Kim books them directly into your schedule. No phone tag, no "I'll call you back."

---

## The Psychology of Persistent Follow-Up

Here's what most practices don't understand: **Patients need to be asked multiple times.**

Studies show:
- **2%** of sales happen on the first contact
- **3%** on the second contact
- **5%** on the third contact
- **10%** on the fourth contact
- **80%** of sales happen between the 5th and 12th contact

Yet **90% of follow-up stops after the 4th attempt.**

Kim doesn't give up. She follows up persistently (but not annoyingly) until the patient schedules, declines, or asks to stop.

---

## Real Results

Practices using AI-powered treatment follow-up see:

| Metric | Before AI | After AI | Improvement |
|--------|-----------|----------|-------------|
| Treatment acceptance rate | 40% | 62% | +55% |
| Average time to schedule | 45 days | 12 days | -73% |
| Unscheduled treatment recovered | $0 | $18,000/month | — |
| Staff time on follow-up | 8 hrs/week | 0 hrs/week | -100% |

### The ROI Calculation

Let's say Kim helps you recover just **25%** of your unscheduled treatment:

- Unscheduled treatment per year: $691,200
- 25% recovered: **$172,800**
- Monthly value: **$14,400**

That's $14,400 in additional production every month—from treatment you've already diagnosed.

---

## What Kim's Follow-Up Looks Like

### Day 2 (Text Message)
> "Hi Sarah! This is Kim from [Practice Name]. Dr. Johnson mentioned you'd like to schedule your crown on tooth #14. I have openings next Tuesday at 2pm or Thursday at 10am. Would either work for you? Reply with your preference or let me know if you have questions!"

### Day 7 (Text Message)
> "Hi Sarah, just checking in! I know life gets busy. That crown Dr. Johnson recommended will protect your tooth from further damage. I can work around your schedule—what days/times are generally best for you?"

### Day 14 (Email)
> Subject: Your Treatment Plan from [Practice Name]
>
> Hi Sarah,
>
> I wanted to follow up on the crown Dr. Johnson recommended for tooth #14. I understand dental work can feel overwhelming, so I wanted to share some information that might help...
>
> [Includes: why the crown matters, what happens if delayed, payment options, sedation options if applicable]

### Day 21 (Phone Call)
Kim calls and has a natural conversation, addressing any concerns and offering to schedule.

---

## The Difference Between Nagging and Caring

There's a fine line between persistent follow-up and annoying patients. Kim walks it perfectly:

### What Feels Like Nagging
- Generic messages
- Too frequent contact
- Ignoring patient responses
- Pressure tactics
- No value in the communication

### What Feels Like Caring
- Personalized messages that reference their specific situation
- Appropriate timing between contacts
- Responding to concerns with helpful information
- Educational content about why treatment matters
- Easy ways to schedule when ready

Kim's follow-up feels like a caring team member who genuinely wants to help—because that's exactly what she is.

---

## Beyond Treatment Plans

Kim doesn't just follow up on crowns and fillings. She handles:

### Specialty Referrals
Patients referred to specialists often fall through the cracks. Kim follows up to ensure they scheduled and completed their specialist visit.

### Phased Treatment
For patients with multiple procedures, Kim tracks what's been completed and follows up on remaining phases.

### Recall for Treatment
Patients who declined treatment in the past might be ready now. Kim periodically re-engages them with updated information.

### Post-Op Follow-Up
After major procedures, Kim checks in to ensure patients are healing well and addresses any concerns.

---

## The Staff Perspective

Your treatment coordinator isn't being replaced—she's being empowered.

**Before Kim:**
- Manually tracking unscheduled treatment
- Making follow-up calls between other tasks
- Feeling guilty about patients who slip through the cracks
- Spending hours on phone tag

**After Kim:**
- Focusing on complex cases that need human attention
- Handling patients who specifically request to speak with staff
- Reviewing Kim's activity and stepping in when needed
- Actually having time to build relationships with patients

---

## Implementation: Easier Than You Think

Getting Kim up and running takes less than a week:

### Day 1-2: Migration
Kim goes live in Asha PMS and begins tracking unscheduled treatment.

### Day 3-4: Customization
You review and customize Kim's messaging templates to match your practice's voice and policies.

### Day 5: Go Live
Kim begins reaching out to patients with unscheduled treatment. Your team monitors and provides feedback.

### Ongoing: Optimization
Kim learns from patient responses and continuously improves her approach.

---

## The Question You Should Be Asking

It's not "Can we afford AI treatment follow-up?"

It's "Can we afford to keep leaving $700,000 on the table every year?"

Every day without systematic follow-up is another day of:
- Lost revenue
- Patients whose conditions worsen
- Treatment that becomes more complex
- Relationships that fade

---

## Your Unscheduled Treatment is Waiting

Right now, in your practice management system, there are patients who need treatment they've already agreed to. They're waiting for someone to reach out. They're waiting for someone to make it easy.

**Kim is ready to reach out today.**

The treatment is diagnosed. The patients are willing. The only thing missing is the follow-up.

Let's fix that.

---

*Ready to recover your unscheduled treatment? [See Kim in action →]*
`;

  return (
    <MarkdownBlogLayout
      title="Case Acceptance: The Revenue You're Leaving on the Table"
      category="Schedule & Production Optimization"
      readTime="6 minutes"
      author="Asha Team"
      date="Jan 12, 2026"
      content={content}
    />
  );
};

export default BlogCaseAcceptanceRevenueLeftBehind;
